import SEO from '../../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, Cpu, Cloud, Shield, Zap, Workflow, Database, Award, Target, Clock, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ProductCTA from '../../components/ProductCTA';
import PersonaNavigation from '../../components/PersonaNavigation';

const ITDirectorPage = () => {
  const stats = [
    { value: "Weeks", label: "Deploy Time" },
    { value: "99.995%", label: "AWS Uptime" },
    { value: "Zero", label: "Server Mgmt" },
    { value: "API", label: "Easy Integration" }
  ];

  const benefits = [
    { icon: Zap, title: "Fast Implementation", strapline: "Weeks not years", desc: "Quick deployment with minimal IT resources using proven methodology." },
    { icon: Cloud, title: "AWS Cloud Managed", strapline: "Zero infrastructure", desc: "We manage SAP B1 on AWS with automatic updates, backups, and 99.995% uptime." },
    { icon: Workflow, title: "Easy Integration", strapline: "Connect seamlessly", desc: "Proven APIs integrate with eCommerce, CRM, payroll, and business apps." },
    { icon: Shield, title: "Enterprise Security", strapline: "Bank-grade protection", desc: "AWS-level security with encryption, role-based access, and audit trails." },
    { icon: Database, title: "Scalable Architecture", strapline: "Grows with you", desc: "SAP HANA in-memory computing scales without performance degradation." },
    { icon: Cpu, title: "Low IT Overhead", strapline: "Minimal resources", desc: "No servers to maintain. Focus your IT team on strategic initiatives." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One for IT Directors | Fast Deployment & Scalability"
        description="Deploy SAP B1 in weeks, not months. Cloud-hosted on AWS with 99.995% uptime. Scalable, secure, easy to manage. Perfect for lean IT teams."
        keywords="IT director ERP, cloud ERP deployment, scalable software, AWS hosting, SAP B1 implementation"
        canonical="https://www.ascarii.com/sap-business-one/for/it-directors"
      />

      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-purple-700 via-indigo-800 to-purple-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=85" alt="Technology" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">For IT Directors</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">Deploy Fast, Scale Seamlessly</h1>
              <p className="text-xl md:text-2xl text-purple-100 mb-8 leading-relaxed">Cloud-managed ERP with proven APIs, AWS reliability, and minimal IT overhead.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-purple-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Persona Navigation */}
      <PersonaNavigation currentPersona="it-directors" />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4 w-fit">AWS Hosting</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Peace of Mind Hosting</h2>
              <p className="text-lg text-gray-600 mb-4">
                AWS hosting delivery gives peace of mind with 99.995% uptime and 24x7 monitoring, allowing you to focus on other areas of your IT estate.
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">Competitive Costs</h4>
                  <p className="text-sm text-gray-700">Unbelievably competitive hosting costs allow your IT budget to stretch further.</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">SAP Innovation</h4>
                  <p className="text-sm text-gray-700">Over EUR 5.7bn invested in R&D annually. Two upgrades per year included.</p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg?w=800&auto=compress" alt="Data Center" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-blue-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-purple-800 via-indigo-900 to-purple-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why IT Directors Choose SAP Business One</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-lg text-cyan-300 mb-3 font-medium">{benefit.strapline}</p>
                    <p className="text-purple-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-100 via-purple-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Transform Your IT Strategy</h2>
              <p className="text-xl text-gray-600 mb-8">Join IT Directors delivering cloud ERP with zero overhead</p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/10 to-indigo-500/10 flex items-center justify-center">
                    <Cloud className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">AWS Cloud</h4>
                    <p className="text-gray-600 text-sm">99.995% uptime</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/10 to-indigo-500/10 flex items-center justify-center">
                    <Zap className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Fast Deploy</h4>
                    <p className="text-gray-600 text-sm">Weeks not months</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/10 to-indigo-500/10 flex items-center justify-center">
                    <Lock className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Secure</h4>
                    <p className="text-gray-600 text-sm">Enterprise protection</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA productName="SAP Business One for IT Directors" brandColor="purple-600" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ITDirectorPage;
